/**
 * Injects / updates head tags from CRA env at runtime (build-time inlined).
 * Configure via afterme-front/.env.production (see .env.example).
 */

function upsertMeta(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    create(el);
    document.head.appendChild(el);
  }
  return el;
}

function upsertMetaProperty(property, content) {
  const el = upsertMeta(`meta[property="${property}"]`, (node) => {
    node.setAttribute("property", property);
  });
  el.setAttribute("content", content);
}

function upsertMetaName(name, content) {
  const el = upsertMeta(`meta[name="${name}"]`, (node) => {
    node.setAttribute("name", name);
  });
  el.setAttribute("content", content);
}

function upsertLinkCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeExistingJsonLd() {
  document.querySelectorAll('script[type="application/ld+json"][data-afterme-seo]').forEach((n) => n.remove());
}

/**
 * Call once before React render. Safe to call with partial env (no-op for missing canonical).
 */
export function applySeoHead() {
  const rawOrigin = (process.env.REACT_APP_CANONICAL_ORIGIN || "").trim();
  const origin = rawOrigin.replace(/\/$/, "");
  if (!origin) {
    return;
  }

  const homeUrl = `${origin}/`;
  upsertLinkCanonical(homeUrl);
  upsertMetaProperty("og:url", homeUrl);

  const ogImage =
    (process.env.REACT_APP_OG_IMAGE_URL || "").trim() || `${origin}/logo512.png`;
  upsertMetaProperty("og:image", ogImage);

  const verify = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim();
  if (verify) {
    upsertMetaName("google-site-verification", verify);
  }

  removeExistingJsonLd();
  const ld = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "AfterMe",
    url: homeUrl,
    description:
      "Canadian digital legacy planning for protected family documents, trusted contacts, and privacy-first release rules.",
    publisher: {
      "@type": "Organization",
      name: "AfterMe",
      url: origin,
    },
  };
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.aftermeSeo = "1";
  script.textContent = JSON.stringify(ld);
  document.head.appendChild(script);
}

/**
 * Optional Google Analytics 4 (measurement ID G-XXXXXXXX). Loads only when env is set.
 */
export function applyGoogleAnalytics() {
  const id = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
  if (!id || typeof document === "undefined") {
    return;
  }
  if (document.querySelector(`script[data-afterme-ga="${id}"]`)) {
    return;
  }
  const gtagSrc = document.createElement("script");
  gtagSrc.async = true;
  gtagSrc.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  gtagSrc.dataset.aftermeGa = id;
  document.head.appendChild(gtagSrc);

  const inline = document.createElement("script");
  inline.dataset.aftermeGa = id;
  inline.textContent = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${id.replace(/'/g, "")}', { send_page_view: true });
`;
  document.head.appendChild(inline);
}
